import React from 'react';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';
import { pmsDocumentTypeField, pmsVesselField } from '../shared/pmsFormFields';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsDocument } from './useData';

function toIsoDate(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string') return value.slice(0, 10);
  return undefined;
}

function stripDocumentPayload(data: Partial<PmsDocument>): Record<string, unknown> {
  const {
    vessel,
    documentType,
    uploadedBy,
    equipmentLinks,
    jobLinks,
    workOrderLinks,
    id,
    createdAt,
    updatedAt,
    uploadedById,
    ...payload
  } = data as Record<string, unknown>;

  if (payload.vesselId === '' || payload.vesselId == null) {
    delete payload.vesselId;
  }
  for (const key of ['effectiveDate', 'expiryDate'] as const) {
    if (payload[key] != null && payload[key] !== '') {
      payload[key] = toIsoDate(payload[key]);
    } else {
      delete payload[key];
    }
  }
  return payload;
}

export const moduleConfig = {
  module: {
    title: 'Documents',
    permissionModuleKey: 'pms_documents',
    route: {
      list: '/pms/pms-documents',
      new: '/pms/pms-documents/new',
      detail: (id: number) => `/pms/pms-documents/${id}`,
      edit: (id: number) => `/pms/pms-documents/${id}/edit`},
    api: { list: '/pms-documents', detail: (id: number) => `/pms-documents/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsDocument[]) => items ,
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New',
        showTitle: false,
        variant: 'primary' as const,
        href: '/pms/pms-documents/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsDocument) => item.id,
  },
  emptyStates: {
    noData: 'No records.',
    noSearchResults: 'No matches.',
    noFilterResults: 'No matches.',
  },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsDocument } = await import('../../../services/mutations');
      await deletePmsDocument(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Documents',
    getTitle: (item: PmsDocument) =>
      item.title
        ? String(item.title)
        : item.documentNumber
          ? String(item.documentNumber)
          : `#${item.id}`},
  form: {
    new: { title: 'New document', submitText: 'Create' },
    edit: { title: 'Edit document', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-4xl',
    create: async (data: Partial<PmsDocument>) => {
      const { createPmsDocument } = await import('../../../services/mutations');
      return createPmsDocument(stripDocumentPayload(data));
    },
    update: async (id: number, data: Partial<PmsDocument>) => {
      const { updatePmsDocument } = await import('../../../services/mutations');
      const payload = stripDocumentPayload(data);
      if (data.vesselId === '' || data.vesselId == null) {
        payload.vesselId = null;
      }
      if (!data.effectiveDate) payload.effectiveDate = null;
      if (!data.expiryDate) payload.expiryDate = null;
      return updatePmsDocument(id, payload);
    }},
  fields: [
    {
      ...pmsVesselField(false),
      required: false,
      clearable: true,
      placeholder: 'None — fleet / shared document',
      helperText: 'Optional. Leave empty for documents not tied to one vessel'},
    {
      ...pmsDocumentTypeField(true),
      helperText: 'e.g. Manual, Drawing, Procedure, Certificate'},
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'e.g. Main engine operation manual',
      validation: { required: true }},
    {
      name: 'documentNumber',
      label: 'Document number',
      type: 'text',
      placeholder: 'e.g. MAN-6S50ME-C-OM'},
    {
      name: 'revision',
      label: 'Revision',
      type: 'text',
      placeholder: 'e.g. Rev. 3 / 2024-06'},
    {
      name: 'filePath',
      label: 'File path',
      type: 'text',
      placeholder: 'Server or share path to the file',
      helperText: 'Internal storage path if the file is hosted on the network'},
    {
      name: 'externalUrl',
      label: 'External URL',
      type: 'text',
      placeholder: 'https://…',
      helperText: 'Link to an external document library or maker portal'},
    {
      name: 'manufacturer',
      label: 'Manufacturer',
      type: 'text',
      placeholder: 'e.g. MAN Energy Solutions'},
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      placeholder: 'e.g. 6S50ME-C'},
    {
      name: 'effectiveDate',
      label: 'Effective date',
      type: 'date',
      helperText: 'When this revision became valid'},
    {
      name: 'expiryDate',
      label: 'Expiry date',
      type: 'date',
      helperText: 'Optional review or certificate expiry'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'classification',
        title: 'Classification',
        description: 'Vessel scope and document type.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'documentTypeId'],
        fieldSpans: {
          vesselId: 'md:col-span-6',
          documentTypeId: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'identity',
        title: 'Document',
        description: 'Title and revision identity used when linking to equipment or jobs.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['title', 'documentNumber', 'revision'],
        fieldSpans: {
          title: 'md:col-span-12',
          documentNumber: 'md:col-span-8',
          revision: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'location',
        title: 'File location',
        description: 'Where the document can be opened from.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['filePath', 'externalUrl'],
        fieldSpans: {
          filePath: 'md:col-span-12',
          externalUrl: 'md:col-span-12'},
        collapsible: false},
      {
        id: 'maker',
        title: 'Maker reference',
        description: 'Optional manufacturer and model this document applies to.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['manufacturer', 'model'],
        fieldSpans: {
          manufacturer: 'md:col-span-6',
          model: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'validity',
        title: 'Validity',
        description: 'Effective and expiry dates for controlled documents.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['effectiveDate', 'expiryDate'],
        fieldSpans: {
          effectiveDate: 'md:col-span-6',
          expiryDate: 'md:col-span-6'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const relName = (
  rel: { code?: string | null; name?: string | null; vesselName?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  if (rel?.vesselName) return rel.vesselName;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelVessel = (item: PmsDocument) => {
  const v = item.vessel as { vesselName?: string; name?: string } | null | undefined;
  if (!v && item.vesselId == null) return 'Shared';
  return v?.vesselName ?? v?.name ?? (item.vesselId != null ? `#${item.vesselId}` : 'Shared');
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsDocument>[] => [
  { key: 'title', header: 'Title', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'documentTypeId',
    header: 'Type',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={relName(
          row.documentType as { code?: string; name?: string } | null,
          row.documentTypeId as number | null,
        )}
      />
    )},
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={labelVessel(row)} />},
  {
    key: 'revision',
    header: 'Revision',
    sortable: true,
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
];

export const getDetailSections = (item: PmsDocument, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: labelVessel(item) },
      {
        label: 'Document type',
        value: relName(
          item.documentType as { code?: string; name?: string } | null,
          item.documentTypeId as number | null,
        )},
      { label: 'Title', value: item.title != null ? String(item.title) : '-' },
      { label: 'Document number', value: item.documentNumber != null ? String(item.documentNumber) : '-' },
      { label: 'Revision', value: item.revision != null ? String(item.revision) : '-' },
      { label: 'File path', value: item.filePath != null ? String(item.filePath) : '-' },
      { label: 'External URL', value: item.externalUrl != null ? String(item.externalUrl) : '-' },
      { label: 'Manufacturer', value: item.manufacturer != null ? String(item.manufacturer) : '-' },
      { label: 'Model', value: item.model != null ? String(item.model) : '-' },
      {
        label: 'Effective date',
        value: item.effectiveDate ? formatDate(String(item.effectiveDate)) : '-'},
      {
        label: 'Expiry date',
        value: item.expiryDate ? formatDate(String(item.expiryDate)) : '-'},
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsDocument> => ({
  vesselId: undefined,
  documentTypeId: undefined,
  title: '',
  documentNumber: '',
  revision: '',
  filePath: '',
  externalUrl: '',
  manufacturer: '',
  model: '',
  effectiveDate: undefined,
  expiryDate: undefined});
export const mapItemToFormData = (item: PmsDocument): Partial<PmsDocument> => ({
  vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
  documentTypeId: item.documentTypeId != null ? Number(item.documentTypeId) : undefined,
  vessel: item.vessel ?? undefined,
  documentType: item.documentType ?? undefined,
  title: item.title != null ? String(item.title) : '',
  documentNumber: item.documentNumber != null ? String(item.documentNumber) : '',
  revision: item.revision != null ? String(item.revision) : '',
  filePath: item.filePath != null ? String(item.filePath) : '',
  externalUrl: item.externalUrl != null ? String(item.externalUrl) : '',
  manufacturer: item.manufacturer != null ? String(item.manufacturer) : '',
  model: item.model != null ? String(item.model) : '',
  effectiveDate: item.effectiveDate ? String(item.effectiveDate).slice(0, 10) : undefined,
  expiryDate: item.expiryDate ? String(item.expiryDate).slice(0, 10) : undefined});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
